import { TZDateMini } from '@date-fns/tz';
import { FormattedMessage, FormattedRelativeTime } from 'react-intl';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useIntervalCallback } from '../../time/useIntervalCallback.js';
import { DataTestId } from '../../types/data-props.js';
import { MaskingProps } from '../../types/masking-props.js';
import { StylingProps } from '../../types/styling-props.js';

const MilliSecondsInfo = {
  OneMinute: 60_000,
  OneHour: 3_600_000,
  OneDay: 86_400_000,
} as const;

/**
 * Props for the TimeIndicator component.
 * @internal
 */
export interface TimeIndicatorProps
  extends StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * The timestamp in milliseconds when the last update happened.
   */
  time: number;
}

/**
 * Displays a relative time indicator for a given timestamp.
 * Shows "Just now" for recent times, otherwise uses minutes/hours/days.
 * @internal
 */
export const TimeIndicator = (props: TimeIndicatorProps) => {
  const {
    time,
    'data-testid': dataTestId,
    className: consumerClassName,
    style: consumerStyle,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;
  const date = TZDateMini.tz(getTimezone());

  const { value: now } = useIntervalCallback(
    () => date.getTime(),
    [],
    (time - date.getSeconds() * 1000) % MilliSecondsInfo.OneMinute,
    MilliSecondsInfo.OneMinute,
  );

  const currentTime = now ?? date.getTime();
  const diff = currentTime - time;

  let content;

  if (diff < MilliSecondsInfo.OneMinute) {
    content = (
      <FormattedMessage
        defaultMessage="Just now"
        id="HPbv8ZQrbBL7hRCT"
        description="Just now marker in the FilterField recent suggestions"
      />
    );
  } else if (diff < MilliSecondsInfo.OneHour) {
    const minutes = -Math.floor(diff / MilliSecondsInfo.OneMinute);
    content = (
      // eslint rule is not smart in checking this. The style prop comes from the lib.Intl types, where it defines the style of the formatted value.
      // eslint-disable-next-line react/style-prop-object
      <FormattedRelativeTime value={minutes} unit="minute" style="narrow" />
    );
  } else if (diff < MilliSecondsInfo.OneDay) {
    const hours = -Math.floor(diff / MilliSecondsInfo.OneHour);
    content = (
      // eslint rule is not smart in checking this. The style prop comes from the lib.Intl types, where it defines the style of the formatted value.
      // eslint-disable-next-line react/style-prop-object
      <FormattedRelativeTime value={hours} unit="hour" style="narrow" />
    );
  } else {
    const days = -Math.floor(diff / MilliSecondsInfo.OneDay);
    // eslint rule is not smart in checking this. The style prop comes from the lib.Intl types, where it defines the style of the formatted value.
    // eslint-disable-next-line react/style-prop-object
    content = <FormattedRelativeTime value={days} unit="day" style="narrow" />;
  }

  return (
    <span
      className={consumerClassName}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...remainingProps}
    >
      {content}
    </span>
  );
};
